import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, AuthUser } from '@/services/authService';
import { UserRole } from '@/types/enums';
import { Permission, roleCan } from '@/lib/permissions';

/** Mode tampilan: staf (PENGELOLA/ADMIN_BANK_SAMPAH) dapat berpindah ke
 *  tampilan nasabah ("Mode Nasabah") lalu kembali ke mode aslinya.
 *  Ini murni lapisan UX — role asli di server/RLS tidak berubah. */
type ViewMode = 'default' | 'nasabah';

const VIEW_MODE_STORAGE_KEY = 'bs_view_mode';

/** Role yang boleh masuk Mode Nasabah (punya profil nasabah otomatis). */
const NASABAH_MODE_ROLES: readonly UserRole[] = [
  UserRole.PENGELOLA,
  UserRole.ADMIN_BANK_SAMPAH,
];

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: readonly UserRole[]) => boolean;
  can: (permission: Permission) => boolean;
  /** Role efektif untuk tampilan: NASABAH bila staf sedang di Mode Nasabah. */
  effectiveRole: UserRole | undefined;
  /** true bila staf sedang melihat aplikasi sebagai nasabah. */
  isNasabahMode: boolean;
  /** true bila role user boleh berpindah ke Mode Nasabah. */
  canEnterNasabahMode: boolean;
  enterNasabahMode: () => void;
  exitNasabahMode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'nasabah' ? 'nasabah' : 'default';
    } catch {
      return 'default';
    }
  });

  // Check for existing session on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      const { user: currentUser, error } = await authService.getCurrentUser();
      
      if (error) {
        setError(error);
        setUser(null);
      } else {
        setUser(currentUser);
        setError(null);
      }
    } catch (err) {
      setError('Gagal memeriksa sesi pengguna');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { user: loggedInUser, error } = await authService.login({ email, password });

      if (error) {
        setError(error);
        return false;
      }

      setUser(loggedInUser);
      return true;
    } catch (err) {
      setError('Login gagal');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await authService.loginWithGoogle();

      if (error) {
        setError(error);
        return false;
      }

      // OAuth will redirect, so we return true
      return true;
    } catch (err) {
      setError('Login dengan Google gagal');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setError(null);
      // Bersihkan Mode Nasabah agar sesi berikutnya mulai dari mode asli.
      setViewMode('default');
      try {
        localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
      } catch {
        /* abaikan — storage tidak tersedia */
      }
    } catch (err) {
      setError('Logout gagal');
    } finally {
      setLoading(false);
    }
  };

  const canEnterNasabahMode = !!user && NASABAH_MODE_ROLES.includes(user.role);
  const isNasabahMode = canEnterNasabahMode && viewMode === 'nasabah';
  const effectiveRole: UserRole | undefined = isNasabahMode ? UserRole.NASABAH : user?.role;

  const applyViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      if (mode === 'nasabah') localStorage.setItem(VIEW_MODE_STORAGE_KEY, 'nasabah');
      else localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
    } catch {
      /* abaikan — storage tidak tersedia */
    }
  };

  const enterNasabahMode = () => {
    if (canEnterNasabahMode) applyViewMode('nasabah');
  };

  const exitNasabahMode = () => {
    applyViewMode('default');
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: readonly UserRole[]): boolean => {
    return !!user && roles.includes(user.role);
  };

  const can = (permission: Permission): boolean => {
    return roleCan(user?.role, permission);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    hasRole,
    hasAnyRole,
    can,
    effectiveRole,
    isNasabahMode,
    canEnterNasabahMode,
    enterNasabahMode,
    exitNasabahMode
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
